import React, { ReactElement, useEffect, useState } from "react"
import { Form, Input, Modal, Select } from "antd"
import { useTranslation } from "react-i18next"
import { useZones } from "@/hooks/useZones"
import { isEmpty } from "lodash"
import { currencyAtom } from "@/atoms/currencyAtom"
import { useRecoilValue } from "recoil"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { DayTimeRangeForm } from "@/UI/Forms/FormItems/DayTimeRange"
import DateRangeFilter from "@/UI/Filters/DateRangeFilter"
import { DateFilterType, disabledDate, LUXON_PICKER_DATE_FORMAT } from "@/utils/datetime"
import { DateTime } from "luxon"
import useTimezone from "@/hooks/useTimezone"
import { locationsSelector } from "@/atoms/userAtom"

interface DriverPayIncentiveFormModalProps {
  button: ReactElement
}

const DriverPayIncentiveModal: React.FC<DriverPayIncentiveFormModalProps> = ({ button }) => {
  const { t } = useTranslation(["common"])
  const [form] = Form.useForm()
  const tz = useTimezone()
  const { zones } = useZones()
  const locations = useRecoilValue(locationsSelector)
  const currency = useRecoilValue(currencyAtom)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [dates, setDates] = useState<DateFilterType>({
    range: [
      DateTime.now().startOf("day").toFormat(LUXON_PICKER_DATE_FORMAT),
      DateTime.now().plus({ days: 7 }).endOf("day").toFormat(LUXON_PICKER_DATE_FORMAT),
    ],
    format: LUXON_PICKER_DATE_FORMAT,
  })
  const incentiveType = Form.useWatch("incentiveType", form)

  const showModal = () => {
    setIsModalVisible(true)
  }

  useEffect(() => {
    if (!isModalVisible) {
      form.resetFields()
    }
  }, [isModalVisible])

  const incentiveTypeOptions = [
    {
      value: "orders",
      label: t("common.numberOfOrders"),
    },
    {
      value: "pickup",
      label: t("common.form.surgePickupLocation"),
    },
    ...(!isEmpty(zones)
      ? [
          {
            value: "zone",
            label: t("common.surgeZone"),
          },
        ]
      : []),
  ]

  let formInputs
  switch (incentiveType) {
    case "zone":
      const zonesOptions = zones.map((zone) => ({
        label: zone?.name,
        value: zone.id,
        key: zone.id,
      }))
      formInputs = (
        <>
          <Form.Item name="zones" label={t("common.form.zones")}>
            <Select
              id="select-zones"
              className="cursor-pointer"
              options={zonesOptions}
              allowClear
              popupMatchSelectWidth={false}
              size="large"
              mode="multiple"
              style={{ width: "100%" }}
              placeholder={t("common.form.selectZones")}
              dropdownRender={(menu) => <>{menu}</>}
            />
          </Form.Item>
          <Form.Item name="incentivePeriod" label="Incentive period">
            <DateRangeFilter
              selectTimeDisabled
              dates={dates}
              setDates={setDates}
              withPresets={false}
              disabledDate={disabledDate(tz)}
            />
          </Form.Item>
          <DayTimeRangeForm
            name="timeOfDay"
            label={t("common.byTimeOfDay")}
            form={form}
            setFieldsChange={() => {}}
          />
        </>
      )
      break
    case "pickup":
      const pickupOptions = locations
        ?.filter((location) => location.alias)
        .map((location) => ({
          label: (
            <div className="flex gap-x-2" title={location?.address}>
              {location?.alias ? (
                <p className="">{location.alias}</p>
              ) : (
                <p className="">{location.name}</p>
              )}
              {/* {
                <p className="text-ellipsis overflow-hidden max-w-[250px]">
                  {location.address?.split(",", 2)?.join(",")}
                </p>
              } */}
            </div>
          ),
          value: location?.id,
          key: location?.id,
        }))
      formInputs = (
        <>
          <Form.Item name="pickup" label={t("common.form.location")}>
            <Select
              id="select-pickup"
              className="cursor-pointer"
              options={pickupOptions}
              allowClear
              popupMatchSelectWidth={true}
              size="large"
              mode="multiple"
              style={{ width: "100%" }}
              placeholder={t("common.form.selectLocations")}
              dropdownRender={(menu) => <>{menu}</>}
            />
          </Form.Item>
          <Form.Item name="incentivePeriod" label="Incentive period">
            <DateRangeFilter
              selectTimeDisabled
              dates={dates}
              setDates={setDates}
              withPresets={false}
              disabledDate={disabledDate(tz)}
            />
          </Form.Item>
          <DayTimeRangeForm
            name="timeOfDay"
            label={t("common.byTimeOfDay")}
            form={form}
            setFieldsChange={() => {}}
          />
        </>
      )
      break
    case "orders":
      formInputs = (
        <>
          <Form.Item name={"orderCount"} label={t("common.numberOfOrders")}>
            <Input
              className="rounded-md border-[#d9d9d9] !max-w-[120px]"
              placeholder={"50"}
              min={0}
              step={1}
              type="number"
              suffix=" "
            />
          </Form.Item>
          <Form.Item name="incentivePeriod" label="Incentive period">
            <DateRangeFilter
              selectTimeDisabled
              dates={dates}
              setDates={setDates}
              withPresets={false}
              disabledDate={disabledDate(tz)}
            />
          </Form.Item>
        </>
      )
      break
    default:
      break
  }

  return (
    <>
      {React.cloneElement(button, { onClick: showModal })}
      <Modal
        open={isModalVisible}
        className="editProfileModal"
        footer={null}
        closable={true}
        title={t("common.incentive")}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose={true}
        zIndex={3000}
      >
        <div className="mt-6 h-full">
          <Form form={form} className="flex flex-col w-full gap-y-2 h-full" layout="vertical">
            <Form.Item
              name="incentiveType"
              className="w-1/2"
              initialValue={"orders"}
              label={t("new-orders.DraftOrderTable.type")}
            >
              <Select
                defaultValue={"orders"}
                options={incentiveTypeOptions}
                popupMatchSelectWidth
              />
            </Form.Item>
            {formInputs}
            <Form.Item name="amount" label={t("common.amount")}>
              <Input
                name="basePrice"
                className="rounded-md border-[#d9d9d9] !max-w-[120px]"
                placeholder={"10"}
                min={0}
                step={0.01}
                type="number"
                suffix={currency?.symbol}
              />
            </Form.Item>
          </Form>
        </div>
        <div className="flex gap-x-4 gap-y-6 justify-end">
          <KosmoButton
            onClick={() => {
              setIsModalVisible && setIsModalVisible(false)
            }}
            type="default"
          >
            {t("common.cancel")}
          </KosmoButton>
          <KosmoButton type="primary" htmlType="submit" onClick={() => setIsModalVisible(false)}>
            {t("common.save")}
          </KosmoButton>
        </div>
      </Modal>
    </>
  )
}

export default DriverPayIncentiveModal
